/**
 * Theme Loader Utility
 * Loads CSS files at runtime via <link> tags from the server
 * No rebuild needed - just upload CSS files via cPanel
 * Priority: Group CSS > Hotel CSS > Fallback (inline)
 *
 * CSS File Structure (upload to server at /assets/css/css-v1/):
 * - Hotel-wise: availability-css/root-{hotelId}.css
 * - Hotel-wise: availability-styles-{hotelId}.css
 * - Group-wise: group-root-css/root-group-{groupId}.css
 * - Group-wise: group-css/availability-styles-group-{groupId}.css
 */

/**
 * Detect the correct base path for CSS files at runtime
 * Handles apps deployed at subpaths (e.g., /booking-engine/)
 */
const getBasePath = () => {
  // Method 1: PUBLIC_URL from build
  if (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) {
    const publicUrl = process.env.PUBLIC_URL;
    const cleanUrl = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl;
    return `${cleanUrl}/assets/css/css-v1`;
  }

  // Method 2: <base> tag
  const baseTag = document.querySelector('base');
  if (baseTag && baseTag.href) {
    try {
      const baseUrl = new URL(baseTag.href);
      const basePath = baseUrl.pathname.endsWith('/')
        ? baseUrl.pathname.slice(0, -1)
        : baseUrl.pathname;
      return `${basePath}/assets/css/css-v1`;
    } catch (e) {}
  }

  // Method 3: Detect from current URL path
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean);
  const routes = ['desktop', 'mobile', 'checkout', 'payment', 'unified-checkout', 'confirmation', 'new-mobile', 'booking-details', 'checkout-page', 'ai-price-match', 'city', 'showcase'];
  const routeIndex = pathParts.findIndex(part => routes.includes(part));

  if (routeIndex > 0) {
    const baseDir = '/' + pathParts.slice(0, routeIndex).join('/');
    return `${baseDir}/assets/css/css-v1`;
  }

  // Method 4: First path segment as app base
  if (pathParts.length > 0) {
    const firstPart = pathParts[0];
    if (!routes.includes(firstPart) && firstPart !== 'static') {
      return `/${firstPart}/assets/css/css-v1`;
    }
  }

  // Fallback
  return '/assets/css/css-v1';
};

/**
 * Inject fallback CSS variables as inline <style> tag
 * This is guaranteed to work on all servers (no external file dependency)
 */
const injectFallbackCSS = () => {
  if (document.getElementById('fallback-theme-vars')) return;

  const style = document.createElement('style');
  style.id = 'fallback-theme-vars';
  style.setAttribute('data-theme-css', 'true');
  style.setAttribute('data-fallback-css', 'true');
  style.textContent = `
    :root {
      --primary-purple: #C8963E;
      --primary-color: #1B3A4B;
      --primary-dark: #122933;
      --light-purple: #B8862D;
      --bgButton: #C8963E;
      --bgCalender: #FFFFFF;
      --eco-green: #2E8B57;
      --success-green: #28A745;
      --error-red: #DC3545;
      --secondary-color: #C8963E;
      --accent-color: #C8963E;
      --success-color: #28A745;
      --warning-color: #F39C12;
      --error-color: #DC3545;
      --text-primary: #2C3E50;
      --text-secondary: #6C757D;
      --text-light: #95A5A6;
      --bg-primary: #FFFFFF;
      --bg-secondary: #F8F9FA;
      --background-light: #F8F9FA;
      --bg-dark: #1B3A4B;
      --white: #FFFFFF;
      --border-light: #E9ECEF;
      --border-medium: #DEE2E6;
      --shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      --shadow-hover: 0 8px 25px rgba(0, 0, 0, 0.15);
      --member-banner-bg: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      --member-banner-border: #93c5fd;
      --member-banner-text: #1e40af;
      --member-banner-btn: #2563eb;
      --promo-badge-bg: #fbbf24;
      --promo-badge-text: #78350f;
      --promo-badge-border: #d97706;
      --promo-badge-icon: #78350f;
    }
  `;
  // Insert at beginning of head so hotel CSS can override
  const firstChild = document.head.firstChild;
  if (firstChild) {
    document.head.insertBefore(style, firstChild);
  } else {
    document.head.appendChild(style);
  }
};

/**
 * Remove previously imported theme styles (except fallback)
 */
const removeExistingThemeCSS = () => {
  const existing = document.querySelectorAll('style[data-theme-css]:not([data-fallback-css])');
  existing.forEach(el => {
    try { el.remove(); } catch (e) {}
  });
  const existingLinks = document.querySelectorAll('link[data-theme-css]:not([data-fallback-css])');
  existingLinks.forEach(link => {
    try { link.remove(); } catch (e) {}
  });
};

/**
 * Load a CSS file at runtime via <link> tag
 * Returns true if loaded successfully, false if file not found (404)
 */
const loadCSSFile = (url) => {
  return new Promise((resolve) => {
    // IMPORTANT: the server serves an SPA fallback (index.html, HTTP 200) for any
    // missing path. A plain <link> onload therefore fires "success" for a CSS file
    // that does not exist (it actually got HTML), which made themeLoader believe a
    // group/hotel theme loaded and SKIP the real fallback → wrong theme.
    // Fix: verify the response is genuinely CSS (2xx + text/css) BEFORE injecting.
    fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        const contentType = (res.headers.get('content-type') || '').toLowerCase();
        if (!res.ok || !contentType.includes('css')) {
          console.log(`%c✗ Theme NOT FOUND (missing / not CSS): ${url}`, 'color: orange');
          resolve(false);
          return;
        }
        // Real CSS — inject as <link> (served from cache after this fetch).
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = url;
        link.setAttribute('data-theme-css', 'true');
        link.onload = () => {
          console.log(`%c✓ Theme LOADED: ${url}`, 'color: green; font-weight: bold');
          resolve(true);
        };
        link.onerror = () => {
          link.remove();
          console.log(`%c✗ Theme link error: ${url}`, 'color: orange');
          resolve(false);
        };
        document.head.appendChild(link);
      })
      .catch(() => {
        console.log(`%c✗ Theme fetch error: ${url}`, 'color: orange');
        resolve(false);
      });
  });
};

/**
 * Version token for cache-busting theme CSS. Theme files have FIXED names
 * (root-<id>.css) so Cloudflare caches them (4h) — and tying the version to the
 * JS bundle hash fails to bust on CSS-only edits (bundle unchanged). Use a
 * per-page-load timestamp so every load fetches fresh theme CSS. Files are tiny
 * (~1.5KB) so the lost CDN caching is negligible, and edits show immediately.
 * Computed once per page load so all theme files in a load share one token.
 */
let _assetVersion = null;
const getAssetVersion = () => {
  if (_assetVersion === null) {
    try {
      _assetVersion = String(Date.now());
    } catch {
      _assetVersion = '0';
    }
  }
  return _assetVersion;
};

/**
 * Build the CSS URL and load it via <link> tag
 * Returns true if loaded, false if not found
 */
const importCSS = async (type, id) => {
  const basePath = getBasePath();
  let path = '';
  switch (type) {
    case 'group-root':
      path = `${basePath}/group-root-css/root-group-${id}.css`;
      break;
    case 'group-styles':
      path = `${basePath}/group-css/availability-styles-group-${id}.css`;
      break;
    case 'hotel-root':
      path = `${basePath}/availability-css/root-${id}.css`;
      break;
    case 'hotel-styles':
      path = `${basePath}/availability-styles-${id}.css`;
      break;
    default:
      return false;
  }
  // Cache-bust: theme CSS files have FIXED names (root-<id>.css) so Cloudflare
  // caches them (max-age 4h). Tie a version to the deployed main.<hash>.js bundle
  // so each deploy serves fresh theme CSS, while staying cached between deploys.
  const sep = path.includes('?') ? '&' : '?';
  return loadCSSFile(`${path}${sep}v=${getAssetVersion()}`);
};

/**
 * Load theme CSS files for a specific hotel ID and group ID
 * Priority: Group CSS > Hotel CSS > Fallback (inline)
 */
export const loadThemeCSS = (hotelId, groupId = null) => {
  if (!hotelId) return;

  // ALWAYS inject fallback CSS first
  injectFallbackCSS();

  const groupIdStr = groupId ? String(groupId).trim() : null;
  const isValidGroupId = groupIdStr &&
                         groupIdStr !== '0' &&
                         groupIdStr !== '' &&
                         groupIdStr !== 'null' &&
                         groupIdStr !== 'undefined' &&
                         !isNaN(parseInt(groupIdStr));

  console.log('Theme: hotelId =', hotelId, '| groupId =', groupId, '| isValidGroupId =', isValidGroupId);

  if (isValidGroupId) {
    // Try Group CSS first
    console.log('%c🎨 Theme: Trying GROUP CSS first, groupId =', 'color: blue; font-weight: bold', groupIdStr);
    Promise.all([
      importCSS('group-root', groupIdStr),
      importCSS('group-styles', groupIdStr)
    ]).then(([rootOk, stylesOk]) => {
      if (!rootOk && !stylesOk) {
        // Group CSS not found → fall back to Hotel CSS
        console.log('%c🎨 Theme: Group CSS not found, falling back to HOTEL CSS', 'color: orange; font-weight: bold');
        Promise.all([
          importCSS('hotel-root', hotelId),
          importCSS('hotel-styles', hotelId)
        ]).then(([hRootOk, hStylesOk]) => {
          if (!hRootOk && !hStylesOk) {
            console.log('%c🎨 Theme: Using FALLBACK CSS (inline)', 'color: red; font-weight: bold');
          } else {
            console.log('%c🎨 Theme: HOTEL CSS loaded for hotelId=' + hotelId, 'color: green; font-weight: bold');
          }
        });
      } else {
        console.log('%c🎨 Theme: GROUP CSS loaded for groupId=' + groupIdStr, 'color: green; font-weight: bold');
      }
    });
  } else {
    // No group → load Hotel CSS directly
    console.log('%c🎨 Theme: Loading HOTEL CSS, hotelId =', 'color: blue; font-weight: bold', hotelId);
    Promise.all([
      importCSS('hotel-root', hotelId),
      importCSS('hotel-styles', hotelId)
    ]).then(([rootOk, stylesOk]) => {
      if (!rootOk && !stylesOk) {
        console.log('%c🎨 Theme: Using FALLBACK CSS (inline)', 'color: red; font-weight: bold');
      } else {
        console.log('%c🎨 Theme: HOTEL CSS loaded for hotelId=' + hotelId, 'color: green; font-weight: bold');
      }
    });
  }
};

/**
 * Get hotel ID and group ID from URL, accommodation detail, or accommodation group detail
 */
export const getThemeIds = (searchParams, accommodationDetail = null, accommodationGroupDetail = null) => {
  const hotelId = searchParams?.get('hotel_id') ||
                  searchParams?.get('accommodationid') ||
                  null;

  let groupId = null;

  // Priority 1: accommodationGroupDetail
  if (accommodationGroupDetail) {
    groupId = accommodationGroupDetail?.groupId ||
              accommodationGroupDetail?.GroupId ||
              accommodationGroupDetail?.GroupID ||
              accommodationGroupDetail?.group_id ||
              accommodationGroupDetail?.hotelMapping?.GroupId ||
              accommodationGroupDetail?.hotelMapping?.groupId ||
              accommodationGroupDetail?.hotelMapping?.GroupID ||
              accommodationGroupDetail?.hotelmapping?.GroupId ||
              accommodationGroupDetail?.hotelmapping?.groupId ||
              accommodationGroupDetail?.hotelmapping?.GroupID ||
              accommodationGroupDetail?.accommodationDetails?.[0]?.GroupId ||
              accommodationGroupDetail?.accommodationDetails?.[0]?.groupId ||
              accommodationGroupDetail?.accommodationDetails?.[0]?.GroupID ||
              accommodationGroupDetail?.accommodationdetailsdb?.[0]?.GroupId ||
              accommodationGroupDetail?.accommodationdetailsdb?.[0]?.groupId ||
              accommodationGroupDetail?.accommodationdetailsdb?.[0]?.GroupID ||
              null;
  }

  // Priority 2: accommodationDetail
  if (!groupId && accommodationDetail) {
    groupId = accommodationDetail?.GroupID ||
              accommodationDetail?.GroupId ||
              accommodationDetail?.groupId ||
              accommodationDetail?.Group_ID ||
              accommodationDetail?.group_id ||
              null;
  }

  const result = {
    hotelId: hotelId ? String(hotelId) : null,
    groupId: groupId ? String(groupId) : null
  };
  console.log('Theme getThemeIds: RESULT =', result);
  return result;
};

/**
 * Initialize theme (non-promise version)
 */
export const initializeTheme = (searchParams, accommodationDetail = null, accommodationGroupDetail = null) => {
  try {
    const { hotelId, groupId } = getThemeIds(searchParams, accommodationDetail, accommodationGroupDetail);
    if (hotelId) {
      loadThemeCSS(hotelId, groupId);
      return { hotelId, groupId };
    } else {
      injectFallbackCSS();
      return { hotelId: null, groupId: null };
    }
  } catch (error) {
    injectFallbackCSS();
    return { hotelId: null, groupId: null };
  }
};

/**
 * Load theme CSS (Promise-based) - waits for CSS to load before resolving
 * Priority: Group CSS > Hotel CSS > Fallback (inline)
 */
export const loadThemeCSSPromise = (hotelId, groupId = null) => {
  return new Promise(async (resolve) => {
    // ALWAYS inject fallback first
    injectFallbackCSS();

    if (!hotelId) {
      resolve();
      return;
    }

    removeExistingThemeCSS();

    const groupIdStr = groupId ? String(groupId).trim() : null;
    const isValidGroupId = groupIdStr &&
                           groupIdStr !== '0' &&
                           groupIdStr !== '' &&
                           groupIdStr !== 'null' &&
                           groupIdStr !== 'undefined' &&
                           !isNaN(parseInt(groupIdStr));

    console.log('%c🎨 ThemePromise: hotelId =', 'color: blue', hotelId, '| groupId =', groupId, '| isValidGroupId =', isValidGroupId);

    try {
      if (isValidGroupId) {
        // Try Group CSS first
        console.log('%c🎨 ThemePromise: Trying GROUP CSS, groupId =', 'color: blue; font-weight: bold', groupIdStr);
        const [rootOk, stylesOk] = await Promise.all([
          importCSS('group-root', groupIdStr),
          importCSS('group-styles', groupIdStr)
        ]);

        if (!rootOk && !stylesOk) {
          // Group CSS not found → fall back to Hotel CSS
          console.log('%c🎨 ThemePromise: Group CSS not found, falling back to HOTEL CSS', 'color: orange; font-weight: bold');
          const [hRootOk, hStylesOk] = await Promise.all([
            importCSS('hotel-root', hotelId),
            importCSS('hotel-styles', hotelId)
          ]);
          if (!hRootOk && !hStylesOk) {
            console.log('%c🎨 ThemePromise: Using FALLBACK CSS (inline)', 'color: red; font-weight: bold');
          } else {
            console.log('%c🎨 ThemePromise: HOTEL CSS loaded for hotelId=' + hotelId, 'color: green; font-weight: bold');
          }
        } else {
          console.log('%c🎨 ThemePromise: GROUP CSS loaded for groupId=' + groupIdStr, 'color: green; font-weight: bold');
        }
      } else {
        // No group → load Hotel CSS
        console.log('%c🎨 ThemePromise: Loading HOTEL CSS, hotelId =', 'color: blue; font-weight: bold', hotelId);
        const [rootOk, stylesOk] = await Promise.all([
          importCSS('hotel-root', hotelId),
          importCSS('hotel-styles', hotelId)
        ]);
        if (!rootOk && !stylesOk) {
          console.log('%c🎨 ThemePromise: Using FALLBACK CSS (inline)', 'color: red; font-weight: bold');
        } else {
          console.log('%c🎨 ThemePromise: HOTEL CSS loaded for hotelId=' + hotelId, 'color: green; font-weight: bold');
        }
      }
    } catch (error) {
      console.log('%c🎨 ThemePromise: Error loading CSS:', 'color: red', error);
    }

    // Force reflow and small delay for CSS to apply
    if (document.body) { void document.body.offsetHeight; }
    setTimeout(() => resolve(), 100);
  });
};

/**
 * Initialize theme and wait for CSS to load (Promise-based)
 */
export const initializeThemePromise = async (searchParams, accommodationDetail = null, accommodationGroupDetail = null) => {
  try {
    const { hotelId, groupId } = getThemeIds(searchParams, accommodationDetail, accommodationGroupDetail);

    if (hotelId) {
      await loadThemeCSSPromise(hotelId, groupId);
      return { hotelId, groupId };
    } else {
      injectFallbackCSS();
      return { hotelId: null, groupId: null };
    }
  } catch (error) {
    injectFallbackCSS();
    return { hotelId: null, groupId: null };
  }
};

/**
 * Load fallback theme (for backward compatibility)
 */
export const loadModernMobileUICSS = () => {
  injectFallbackCSS();
};
